import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProgress } from '../../../hooks/useProgress';

export interface FreeLesson {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  color: string;
  textbookExplanation: string;
  shortMethod: string;
  keyFormulas: string[];
  workedExamples: {
    question: string;
    textbookSteps: string[];
    shortcut: string;
    answer: string;
  }[];
  practiceQuestions: {
    question: string;
    steps: string[];
    shortcut: string;
    answer: string;
  }[];
}

export interface FreeLessonTopic {
  id: string;
  title: string;
  icon: string;
  color: string;
  lessons: FreeLesson[];
}

const colorMap: Record<string, {
  bg: string; text: string; border: string; light: string;
  badge: string; btn: string; btnHover: string; ring: string;
}> = {
  orange: { bg: 'bg-orange-600', text: 'text-orange-600', border: 'border-orange-500', light: 'bg-orange-50', badge: 'bg-orange-100 text-orange-700', btn: 'bg-orange-600', btnHover: 'hover:bg-orange-700', ring: 'ring-orange-200' },
  teal:   { bg: 'bg-teal-600',   text: 'text-teal-600',   border: 'border-teal-500',   light: 'bg-teal-50',   badge: 'bg-teal-100 text-teal-700',   btn: 'bg-teal-600',   btnHover: 'hover:bg-teal-700',   ring: 'ring-teal-200'   },
  amber:  { bg: 'bg-amber-600',  text: 'text-amber-600',  border: 'border-amber-500',  light: 'bg-amber-50',  badge: 'bg-amber-100 text-amber-700',  btn: 'bg-amber-600',  btnHover: 'hover:bg-amber-700',  ring: 'ring-amber-200'  },
  green:  { bg: 'bg-green-600',  text: 'text-green-600',  border: 'border-green-500',  light: 'bg-green-50',  badge: 'bg-green-100 text-green-700',  btn: 'bg-green-600',  btnHover: 'hover:bg-green-700',  ring: 'ring-green-200'  },
  rose:   { bg: 'bg-rose-600',   text: 'text-rose-600',   border: 'border-rose-500',   light: 'bg-rose-50',   badge: 'bg-rose-100 text-rose-700',   btn: 'bg-rose-600',   btnHover: 'hover:bg-rose-700',   ring: 'ring-rose-200'   },
  violet: { bg: 'bg-violet-600', text: 'text-violet-600', border: 'border-violet-500', light: 'bg-violet-50', badge: 'bg-violet-100 text-violet-700', btn: 'bg-violet-600', btnHover: 'hover:bg-violet-700', ring: 'ring-violet-200' },
  slate:  { bg: 'bg-slate-700',  text: 'text-slate-700',  border: 'border-slate-500',  light: 'bg-slate-50',  badge: 'bg-slate-100 text-slate-700',  btn: 'bg-slate-700',  btnHover: 'hover:bg-slate-800',  ring: 'ring-slate-200'  },
};

function renderText(text: string) {
  const lines = text.split('\n');
  return lines.map((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return (
      <span key={i}>
        {parts.map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={j} className="font-bold text-gray-900">{part.slice(2, -2)}</strong>;
          }
          return <span key={j}>{part}</span>;
        })}
        {i < lines.length - 1 && <br />}
      </span>
    );
  });
}

function PracticeCard({
  q, index, color, isWorked = false,
}: {
  q: FreeLessonTopic['lessons'][0]['practiceQuestions'][0];
  index: number;
  color: string;
  isWorked?: boolean;
}) {
  const [tab, setTab] = useState<'none' | 'steps' | 'shortcut' | 'answer'>('none');
  const c = colorMap[color] || colorMap.orange;

  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all ${isWorked ? `ring-2 ${c.ring}` : ''}`}>
      {isWorked && (
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${c.badge} mb-3`}>
          <i className="ri-star-fill text-xs"></i> Worked Example
        </div>
      )}
      <div className="flex items-start gap-3 mb-4">
        <span className={`w-7 h-7 flex items-center justify-center ${c.bg} text-white rounded-full text-xs font-bold flex-shrink-0`}>
          {index + 1}
        </span>
        <p className="text-gray-800 font-semibold text-sm leading-relaxed">{q.question}</p>
      </div>

      <div className="flex gap-2 flex-wrap ml-10">
        <button
          onClick={() => setTab(tab === 'steps' ? 'none' : 'steps')}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer ${tab === 'steps' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
        >
          <i className="ri-list-ordered mr-1"></i>Full Steps
        </button>
        <button
          onClick={() => setTab(tab === 'shortcut' ? 'none' : 'shortcut')}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer ${tab === 'shortcut' ? `${c.btn} text-white` : `${c.light} ${c.text} hover:opacity-80`}`}
        >
          <i className="ri-flashlight-line mr-1"></i>Short Method
        </button>
        <button
          onClick={() => setTab(tab === 'answer' ? 'none' : 'answer')}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer ${tab === 'answer' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
        >
          <i className="ri-check-double-line mr-1"></i>Answer
        </button>
      </div>

      {tab === 'steps' && (
        <div className="mt-4 ml-10 bg-slate-50 rounded-xl p-4 border-l-4 border-slate-400">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Step-by-Step Solution</p>
          <ol className="space-y-2">
            {q.steps.map((step, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                <span className="w-5 h-5 flex items-center justify-center bg-slate-300 text-slate-800 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                <span className="leading-relaxed">{renderText(step)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {tab === 'shortcut' && (
        <div className={`mt-4 ml-10 ${c.light} rounded-xl p-4 border-l-4 ${c.border}`}>
          <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${c.text}`}>
            <i className="ri-flashlight-fill mr-1"></i>Mr. Augustine's Short Method
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">{renderText(q.shortcut)}</p>
        </div>
      )}

      {tab === 'answer' && (
        <div className="mt-4 ml-10 bg-green-50 rounded-xl p-3 border-l-4 border-green-500">
          <p className="text-xs font-bold text-green-700 uppercase tracking-wider mb-1">Final Answer</p>
          <p className="text-sm font-bold text-green-800">{q.answer}</p>
        </div>
      )}
    </div>
  );
}

function LessonCard({ lesson, color }: { lesson: FreeLesson; color: string }) {
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<'textbook' | 'short' | 'practice'>('textbook');
  const [showAll, setShowAll] = useState(false);
  const c = colorMap[color] || colorMap.orange;
  const displayed = showAll ? lesson.practiceQuestions : lesson.practiceQuestions.slice(0, 5);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-all">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-6 text-left cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className={`w-11 h-11 flex items-center justify-center ${c.bg} rounded-xl flex-shrink-0`}>
            <i className={`${lesson.icon} text-white text-xl`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-bold text-gray-900 mb-0.5">{lesson.title}</h4>
            <p className="text-sm text-gray-500">{lesson.subtitle}</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0 ml-4">
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${c.badge} whitespace-nowrap`}>
              {lesson.practiceQuestions.length} questions
            </span>
            <div className={`w-8 h-8 flex items-center justify-center rounded-full ${c.light}`}>
              <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} ${c.text} text-xl`}></i>
            </div>
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-100">
          {/* Key Formulas */}
          {lesson.keyFormulas.length > 0 && (
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Key Formulas & Rules</p>
              <div className="flex flex-wrap gap-2">
                {lesson.keyFormulas.map((f, i) => (
                  <span key={i} className={`text-xs px-3 py-1.5 rounded-lg font-mono font-semibold ${c.badge}`}>{f}</span>
                ))}
              </div>
            </div>
          )}

          {/* Section Tabs */}
          <div className="px-6 pt-5 pb-0">
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
              {[
                { key: 'textbook', label: 'Textbook Explanation', icon: 'ri-book-2-line' },
                { key: 'short', label: "Short Method", icon: 'ri-flashlight-line' },
                { key: 'practice', label: 'Practice (20 Qs)', icon: 'ri-pencil-line' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveSection(tab.key as typeof activeSection)}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                    activeSection === tab.key
                      ? `${c.btn} text-white shadow-sm`
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className={tab.icon}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Textbook Explanation */}
          {activeSection === 'textbook' && (
            <div className="px-6 py-5 border-b border-gray-100">
              <div className={`${c.light} rounded-2xl p-6 border-l-4 ${c.border}`}>
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-7 h-7 flex items-center justify-center ${c.bg} rounded-lg`}>
                    <i className="ri-book-2-line text-white text-sm"></i>
                  </div>
                  <p className="text-sm font-bold text-gray-800">Textbook Explanation</p>
                </div>
                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                  {renderText(lesson.textbookExplanation)}
                </div>
              </div>

              {/* Worked Examples */}
              {lesson.workedExamples.length > 0 && (
                <div className="mt-5">
                  <p className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <i className={`ri-star-line ${c.text}`}></i> Worked Examples
                  </p>
                  <div className="space-y-3">
                    {lesson.workedExamples.map((ex, i) => (
                      <PracticeCard
                        key={i}
                        q={{ question: ex.question, steps: ex.textbookSteps, shortcut: ex.shortcut, answer: ex.answer }}
                        index={i}
                        color={color}
                        isWorked
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Short Method */}
          {activeSection === 'short' && (
            <div className="px-6 py-5 border-b border-gray-100">
              <div className={`${c.light} rounded-2xl p-6 border-l-4 ${c.border}`}>
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-7 h-7 flex items-center justify-center ${c.bg} rounded-lg`}>
                    <i className="ri-flashlight-line text-white text-sm"></i>
                  </div>
                  <p className="text-sm font-bold text-gray-800">Mr. Augustine's Short Method</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${c.badge}`}>Exclusive</span>
                </div>
                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                  {renderText(lesson.shortMethod)}
                </div>
              </div>
            </div>
          )}

          {/* Practice Questions */}
          {activeSection === 'practice' && (
            <div className="px-6 py-5">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <i className={`ri-pencil-line ${c.text}`}></i>
                  Practice Questions — {lesson.practiceQuestions.length} for Mastery
                </p>
                <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${c.badge}`}>
                  {showAll ? lesson.practiceQuestions.length : Math.min(5, lesson.practiceQuestions.length)} shown
                </span>
              </div>
              <div className="space-y-3">
                {displayed.map((q, i) => (
                  <PracticeCard key={i} q={q} index={i} color={color} />
                ))}
              </div>
              {lesson.practiceQuestions.length > 5 && (
                <button
                  onClick={() => setShowAll(!showAll)}
                  className={`mt-4 w-full py-3 rounded-xl text-sm font-bold ${c.btn} ${c.btnHover} text-white transition-all cursor-pointer whitespace-nowrap`}
                >
                  {showAll
                    ? 'Show Fewer Questions'
                    : `Show All ${lesson.practiceQuestions.length} Questions with Solutions`}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TopicBlock({ topic }: { topic: FreeLessonTopic }) {
  const [open, setOpen] = useState(true);
  const c = colorMap[topic.color] || colorMap.orange;
  const totalQ = topic.lessons.reduce((s, l) => s + l.practiceQuestions.length, 0);

  return (
    <div className="mb-10">
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between p-5 rounded-2xl ${c.bg} text-white cursor-pointer hover:opacity-95 transition-opacity`}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
            <i className={`${topic.icon} text-2xl`}></i>
          </div>
          <div className="text-left">
            <h3 className="text-xl font-bold">{topic.title}</h3>
            <p className="text-white/80 text-sm">{topic.lessons.length} lessons · {totalQ} practice questions</p>
          </div>
        </div>
        <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
          <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl`}></i>
        </div>
      </button>

      {open && (
        <div className="mt-4 space-y-4 pl-2">
          {topic.lessons.map((lesson) => (
            <LessonCard key={lesson.id} lesson={lesson} color={topic.color} />
          ))}
        </div>
      )}
    </div>
  );
}

interface LessonViewerProps {
  courseId: string;
  courseTitle: string;
  courseDescription: string;
  topics: FreeLessonTopic[];
}

export default function LessonViewer({ courseId, courseTitle, courseDescription, topics }: LessonViewerProps) {
  const [selectedLesson, setSelectedLesson] = useState<FreeLesson | null>(null);
  const [showAnswer, setShowAnswer] = useState<{ [key: number]: boolean }>({});
  const { isComplete, toggleComplete, getCourseProgress, getTopicProgress } = useProgress();

  // Calculate total lessons
  const totalLessons = topics.reduce((sum, topic) => sum + topic.lessons.length, 0);
  const courseProgress = getCourseProgress(courseId, totalLessons);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-600 to-amber-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <Link to="/free-lessons" className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors">
            <i className="ri-arrow-left-line"></i>
            <span className="font-medium">Back to All Courses</span>
          </Link>
          <h1 className="text-5xl font-bold mb-4">{courseTitle}</h1>
          <p className="text-xl text-white/90 mb-6 max-w-3xl">{courseDescription}</p>
          
          {/* Course Progress Summary */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 max-w-md">
            <div className="flex items-center justify-between mb-3">
              <span className="text-lg font-semibold">Your Progress</span>
              <span className="text-2xl font-bold">{courseProgress.percentage}%</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-3 mb-2">
              <div 
                className="bg-white rounded-full h-3 transition-all duration-500"
                style={{ width: `${courseProgress.percentage}%` }}
              ></div>
            </div>
            <p className="text-sm text-white/80">
              {courseProgress.completed} of {courseProgress.total} lessons complete
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {!selectedLesson ? (
          <div className="space-y-12">
            {topics.map((topic, topicIndex) => {
              const lessonIds = topic.lessons.map(lesson => lesson.id);
              const topicProgress = getTopicProgress(courseId, lessonIds);
              
              return (
                <div key={topicIndex} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-1">
                        <h2 className="text-3xl font-bold text-white mb-2">{topic.title}</h2>
                        <p className="text-white/90 text-lg">{topic.description}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-white">{topicProgress.percentage}%</div>
                        <div className="text-sm text-white/80">{topicProgress.completed}/{topicProgress.total} done</div>
                      </div>
                    </div>
                    
                    {/* Topic Progress Bar */}
                    <div className="w-full bg-white/20 rounded-full h-2">
                      <div 
                        className="bg-white rounded-full h-2 transition-all duration-500"
                        style={{ width: `${topicProgress.percentage}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="p-6 grid md:grid-cols-2 gap-6">
                    {topic.lessons.map((lesson) => {
                      const completed = isComplete(courseId, lesson.id);
                      
                      return (
                        <div 
                          key={lesson.id}
                          className={`group relative bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border-2 transition-all duration-300 ${
                            completed 
                              ? 'border-green-500 shadow-lg shadow-green-100' 
                              : 'border-gray-200 hover:border-orange-300 hover:shadow-xl'
                          }`}
                        >
                          {/* Complete Badge */}
                          {completed && (
                            <div className="absolute top-4 right-4 bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center">
                              <i className="ri-check-line text-lg"></i>
                            </div>
                          )}

                          <div className="flex items-start gap-4 mb-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-colors ${
                              completed ? 'bg-green-100' : 'bg-orange-100 group-hover:bg-orange-200'
                            }`}>
                              {lesson.icon}
                            </div>
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-900 mb-2">{lesson.title}</h3>
                              <p className="text-gray-600 text-sm leading-relaxed">{lesson.description}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => setSelectedLesson(lesson)}
                              className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
                            >
                              Start Lesson
                            </button>
                            
                            <button
                              onClick={() => toggleComplete(courseId, lesson.id)}
                              className={`px-4 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                                completed
                                  ? 'bg-green-500 text-white hover:bg-green-600'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                              title={completed ? 'Mark as incomplete' : 'Mark as complete'}
                            >
                              <i className={`${completed ? 'ri-check-line' : 'ri-checkbox-blank-circle-line'} text-xl`}></i>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Lesson Header */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-8">
              <button
                onClick={() => {
                  setSelectedLesson(null);
                  setShowAnswer({});
                }}
                className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                <span className="font-medium">Back to Lessons</span>
              </button>
              
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-3xl">
                      {selectedLesson.icon}
                    </div>
                    <div>
                      <h1 className="text-4xl font-bold text-white">{selectedLesson.title}</h1>
                      <p className="text-white/90 text-lg mt-1">{selectedLesson.description}</p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => toggleComplete(courseId, selectedLesson.id)}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    isComplete(courseId, selectedLesson.id)
                      ? 'bg-white text-green-600 hover:bg-green-50'
                      : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
                  }`}
                >
                  <i className={`${isComplete(courseId, selectedLesson.id) ? 'ri-check-line' : 'ri-checkbox-blank-circle-line'} mr-2`}></i>
                  {isComplete(courseId, selectedLesson.id) ? 'Completed' : 'Mark Complete'}
                </button>
              </div>
            </div>

            {/* ... rest of existing lesson detail code ... */}
            <div className="p-8 space-y-8">
              {/* Textbook Explanation */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 border-2 border-blue-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <i className="ri-book-open-line text-white text-xl"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">📚 Textbook Explanation</h2>
                </div>
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed whitespace-pre-line">
                  {selectedLesson.textbookExplanation}
                </div>
              </div>

              {/* Mr. Augustine's Short Method */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8 border-2 border-purple-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                    <i className="ri-lightbulb-flash-line text-white text-xl"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">⚡ Mr. Augustine's Short Method</h2>
                </div>
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed whitespace-pre-line">
                  {selectedLesson.shortMethod}
                </div>
              </div>

              {/* Worked Examples */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 border-2 border-green-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                    <i className="ri-file-list-3-line text-white text-xl"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">📝 Worked Examples</h2>
                </div>
                <div className="space-y-6">
                  {selectedLesson.examples.map((example, index) => (
                    <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Example {index + 1}</h3>
                      <div className="space-y-3">
                        <div>
                          <span className="font-semibold text-gray-700">Problem:</span>
                          <p className="text-gray-800 mt-1">{example.problem}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Solution:</span>
                          <p className="text-gray-800 mt-1 whitespace-pre-line leading-relaxed">{example.solution}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Practice Questions */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-8 border-2 border-orange-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                    <i className="ri-pencil-line text-white text-xl"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">✏️ Practice Questions ({selectedLesson.questions.length} Questions)</h2>
                </div>
                <div className="space-y-4">
                  {selectedLesson.questions.map((question, index) => (
                    <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <h3 className="text-lg font-bold text-gray-900">Question {index + 1}</h3>
                        <button
                          onClick={() => setShowAnswer(prev => ({ ...prev, [index]: !prev[index] }))}
                          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium whitespace-nowrap cursor-pointer"
                        >
                          {showAnswer[index] ? 'Hide Answer' : 'Show Answer'}
                        </button>
                      </div>
                      <p className="text-gray-800 mb-4">{question.question}</p>
                      
                      {showAnswer[index] && (
                        <div className="mt-4 pt-4 border-t-2 border-orange-200 space-y-3">
                          <div>
                            <span className="font-semibold text-green-600">Answer:</span>
                            <p className="text-gray-800 mt-1">{question.answer}</p>
                          </div>
                          <div>
                            <span className="font-semibold text-blue-600">Solution:</span>
                            <p className="text-gray-700 mt-1 whitespace-pre-line leading-relaxed">{question.solution}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
